import { useMsal } from '@azure/msal-react';
import { Button, makeStyles, mergeClasses, Tab, TabList, Text } from '@fluentui/react-components';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import { useStores } from '../../common/hooks';
import { DocumentGroupTypeCode, ProgramDocument } from '../../models';
import { parseAsString, useQuery } from '../../shared/hooks';
import { ConfirmationDialog, getCardThemeTypeByIndex, PageTitle, ProgramDocumentCard } from '../shared-components';
import { FlexBox } from '../shared-components/flex-box';
import Loading from '../shared-components/Loading';
import { AddOrEditProgramDocumentModal } from './components';
import './UserResourcesPage.scss';

const useTabListStyle = makeStyles({
  root: {
    marginLeft: 'auto',
    alignItems: 'center',
  },
  tab: {
    fontSize: '18px',
    lineHeight: '1.5',
  }
});

const UserResourcesPageImpl: React.FC = () => {
  const {
    instance
  } = useMsal();

  const {
    apiStore,
    awardStore,
    userResourcesPageUiStore,
  } = useStores();

  const {
    urlSearchParams,
    getQueryParam,
    updateQuery,
  } = useQuery();

  const styles = useTabListStyle();

  const [isLoading, setLoading] = useState<boolean>(false);
  const [isAddOrEditProgramDocumentModalOpen, setIsAddOrEditProgramDocumentModalOpen] = useState<boolean>(false);
  const [isConfirmRemoveDialogOpen, setIsConfirmRemoveDialogOpen] = useState<boolean>(false);

  const currentDocumentGroup = getQueryParam<DocumentGroupTypeCode>('tab', parseAsString<DocumentGroupTypeCode>) || 'award-resource';

  const setCurrentDocumentGroup = (tab: DocumentGroupTypeCode) => {
    updateQuery(
      { tab },
      { replace: true }
    );
  };

  const getStaffProgramDocuments = async (): Promise<void> => {
    try {
      const staffProgramDocuments = await apiStore.getStaffProgramDocuments(instance, currentDocumentGroup);
      userResourcesPageUiStore.setProgramDocuments(staffProgramDocuments);
    } catch (error) {
      // TODO: Handle error
    }
  };

  const handleRemoveProgramDocument = async (programDocument?: ProgramDocument): Promise<void> => {
    if (!programDocument) return;
    setLoading(true);

    try {
      await apiStore.deleteProgramDocument(instance, programDocument.programDocumentId);
      await getStaffProgramDocuments();

      userResourcesPageUiStore.setSelectedProgramDocument(undefined);
      setIsConfirmRemoveDialogOpen(false);
    } catch (error) {
      // TODO: Handle error
    } finally {
      setLoading(false);
    }
  }

  const onAddNewProgramDocument = (): void => {
    userResourcesPageUiStore.setSelectedProgramDocument(undefined);
    setIsAddOrEditProgramDocumentModalOpen(true);
  }

  const onEditProgramDocument = (programDocument: ProgramDocument): void => {
    userResourcesPageUiStore.setSelectedProgramDocument(programDocument);
    setIsAddOrEditProgramDocumentModalOpen(true);
  }

  const onDismissAddOrEditProgramDocumentModal = (): void => {
    userResourcesPageUiStore.setSelectedProgramDocument(undefined);
    setIsAddOrEditProgramDocumentModalOpen(false);
  }

  const onRemoveProgramDocument = (programDocument: ProgramDocument): void => {
    userResourcesPageUiStore.setSelectedProgramDocument(programDocument);
    setIsConfirmRemoveDialogOpen(true);
  }

  const onCancelRemoveProgramDocument = (): void => {
    userResourcesPageUiStore.setSelectedProgramDocument(undefined);
    setIsConfirmRemoveDialogOpen(false);
  }

  const applyFilters = () => {
    updateQuery(
      {
        ['tab']: currentDocumentGroup
      },
      { replace: true }
    );
  };

  const preloadData = async () => {
    setLoading(true);

    await Promise.allSettled([
      getStaffProgramDocuments(),
    ]);

    setLoading(false);
  }

  useEffect(() => {
    preloadData();
  }, [urlSearchParams]);

  useEffect(() => {
    applyFilters();
    return () => {
      userResourcesPageUiStore.resetStates();
    }
  }, []);

  return (
    <FlexBox
      as='main'
      isFullWidth
      aligns='start'
      direction='column'
      className='user-resources-page'
    >
      {isLoading && <Loading />}
      <PageTitle
        title='User resources'
        subtitle={awardStore.defaultProgramRef?.displayName}
      />
      <Button
        appearance='primary'
        onClick={() => onAddNewProgramDocument()}
      >
        + Add new
      </Button>
      <TabList
        className={mergeClasses(styles.root, 'user-resources-page__tabs')}
        selectedValue={currentDocumentGroup ?? 'award-resource'}
        onTabSelect={(_, tabData) => setCurrentDocumentGroup(tabData.value as DocumentGroupTypeCode ?? currentDocumentGroup)}
      >
        <Tab
          value='award-resource'
          content={{
            className: styles.tab,
          }}
        >
          Award resources
        </Tab>
        <Text weight='bold'>|</Text>
        <Tab
          value='judging-resource'
          content={{
            className: styles.tab,
          }}
        >
          Judging resources
        </Tab>
        <Text weight='bold'>|</Text>
        <Tab
          value='winner-resource'
          content={{
            className: styles.tab,
          }}
        >
          Winner resources
        </Tab>
      </TabList>
      <FlexBox
        isFullWidth
        wrap
        as='section'
        content={userResourcesPageUiStore.currentProgramDocuments(currentDocumentGroup).length ? 'start' : 'center'}
        className='user-resources-page__cards'
      >
        {
          !!userResourcesPageUiStore.currentProgramDocuments(currentDocumentGroup).length
            ?
            userResourcesPageUiStore.currentProgramDocuments(currentDocumentGroup).map((programDocument, index) => (
              <ProgramDocumentCard
                key={programDocument.programDocumentId}
                programDocument={programDocument}
                cardTheme={getCardThemeTypeByIndex(index)}
                isShowWinnerResource={currentDocumentGroup === 'winner-resource'}
                editingMode={{
                  onEditButtonClicked: (programDocument) => onEditProgramDocument(programDocument),
                  onRemoveButtonClicked: (programDocument) => onRemoveProgramDocument(programDocument),
                }}
              />
            ))
            :
            <Text>No resource found.</Text>
        }
      </FlexBox>
      {
        isAddOrEditProgramDocumentModalOpen && (
          <AddOrEditProgramDocumentModal
            isModalOpen={isAddOrEditProgramDocumentModalOpen}
            selectedProgramDocument={userResourcesPageUiStore.selectedProgramDocument}
            onSuccess={() => preloadData()}
            onDismissModal={() => onDismissAddOrEditProgramDocumentModal()}
          />
        )
      }
      <ConfirmationDialog
        isOpen={isConfirmRemoveDialogOpen}
        isLoading={isLoading}
        dialogTitle='Confirm Remove Program Document'
        dialogSubtext={(<p className='fs-6'>Are you sure you want to remove this program document? This action cannot be undone.</p>)}
        onPrimaryButtonClick={() => handleRemoveProgramDocument(userResourcesPageUiStore.selectedProgramDocument)}
        onSecondaryButtonClick={() => onCancelRemoveProgramDocument()}
        primaryButtonLabel='Yes'
        secondaryButtonLabel='No, go back'
        onCloseDialogClick={() => onCancelRemoveProgramDocument()}
      />
    </FlexBox>
  );
};

export const UserResourcesPage = observer(UserResourcesPageImpl);
